import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import {
  CheckCircle,
  Shield,
  Zap,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from 'lucide-react'
import InlineScanSection from '../components/InlineScanSection'
export default function AdaCompliancePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const adaChecklist = [
    {
      issue: 'Missing alternative text',
      description: 'on informative images.'
    },
    {
      issue: 'Low color contrast ratios',
      description: 'that make text difficult to read.'
    },
    {
      issue: 'Inaccessible forms',
      description: 'without clear, programmatically associated labels.'
    },
    {
      issue: 'Keyboard traps',
      description: 'or missing visible focus indicators for keyboard-only users.'
    },
    {
      issue: 'Missing captions',
      description: 'for videos, which excludes deaf or hard-of-hearing users.'
    },
    {
      issue: 'Poor heading and landmark structure',
      description: 'making page navigation confusing for screen reader users.'
    }
  ]
  const adaVsWcag = [
    {
      type: 'ADA',
      description: 'A U.S. civil rights law requiring equal access for people with disabilities.'
    },
    {
      type: 'WCAG',
      description: 'A set of global, technical guidelines for making web content accessible.'
    }
  ]
  const faqs = [
    {
      question: 'What is an ADA compliance checker?',
      answer: 'An ADA compliance checker is an automated tool that scans your website for issues based on the WCAG standard. Since courts use WCAG to measure ADA compliance, this tool helps you identify technical barriers that could lead to discrimination claims under the ADA.'
    },
    {
      question: 'Does the achecker.ca ADA checker guarantee legal compliance?',
      answer: 'No. No automated tool can guarantee legal compliance. Our tool provides an essential technical audit to identify accessibility issues, but full compliance requires manual testing and expert review to ensure a truly usable experience for people with disabilities.'
    },
    {
      question: 'Which WCAG level do I need for ADA?',
      answer: 'WCAG 2.1 Level AA is the standard mandated by the DOJ for public entities and is the de facto standard referenced in legal cases for private businesses. Aiming for this level is the best practice for ADA compliance.'
    },
    {
      question: 'Do all businesses in the US need ADA-compliant websites?',
      answer: 'If your business is considered a "place of public accommodation," then your website must be accessible under the ADA. This applies to a vast majority of businesses, including retail stores, banks, hotels, and healthcare providers.'
    },
    {
      question: 'What are the penalties for non-compliance?',
      answer: 'Penalties for non-compliance with the ADA can include legal fees, costly settlements, and court-ordered remediation. The financial and brand reputation damage from an accessibility lawsuit can be significant.'
    },
    {
      question: 'How does the ADA compliance checker on achecker.ca calculate the score?',
      answer: 'The Accessibility Score is calculated based on the number and severity of WCAG 2.1 AA violations detected on the scanned page. Fewer and less critical issues result in a higher score.'
    },
    {
      question: 'Is this ADA checker free to use?',
      answer: 'Yes. The achecker.ca – free ADA website compliance checker is available for on-demand, single-page scans at no cost.'
    },
    {
      question: 'Does ADA require WCAG 2.1 AA specifically?',
      answer: 'For state and local governments (Title II), yes, the DOJ rule effective from 2026 mandates WCAG 2.1 AA. For private businesses (Title III), it is the universally accepted standard used in legal proceedings to determine compliance.'
    },
    {
      question: 'How is ADA different from Section 508?',
      answer: 'The ADA is a broad civil rights law applying to public and private sectors. Section 508 is a federal law that requires federal agencies and their contractors to have accessible technology. Both now point to WCAG as the technical standard.'
    },
    {
      question: 'What issues can only be caught manually, not by automation?',
      answer: 'Automated tools like our accessibility checker cannot reliably test the quality of alt text, the accuracy of video captions, or the logical flow of a keyboard navigation journey. These context-dependent issues always require human evaluation.'
    }
  ]
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }
  return (
    <>
      <Head>
        <title>Free ADA Website Compliance Checker – achecker.ca</title>
        <meta name="description" content="Run a free ADA compliance checker on achecker.ca. Instant WCAG 2.1 AA audit, Accessibility Score, and fixes for ADA website accessibility." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/ada-compliance-checker')} />
      </Head>
      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-red-50 to-pink-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            ADA Compliance Checker – Free WCAG 2.1 AA Website Audit with Score & Fixes
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Run a free <strong>ADA accessibility checker on achecker.ca</strong>. Instantly test your website for accessibility issues related to the Americans with Disabilities Act (ADA) with our WCAG-based audit. The achecker.ca <strong>ADA compliance checker</strong> provides an immediate Accessibility Score and a clear path to improve your site's usability for everyone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start ADA Check
            </a>
            <Link
              to="/wcag-validator"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              WCAG Validator
            </Link>
          </div>
        </section>
        {/* Inline Scan Section */}
        <InlineScanSection />
        {/* ADA Compliance Checker Overview */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">ADA Compliance Checker by achecker.ca</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Our tool helps you identify technical barriers that could put your website at risk of an ADA complaint. It's important to understand that the ADA is a U.S. civil rights law that prohibits discrimination against individuals with disabilities. While the law itself doesn't contain technical code, its requirements are met by following the Web Content Accessibility Guidelines (WCAG).
            </p>
          </div>
        </section>
        {/* What ADA Means for Websites */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What ADA Means for Websites</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              The ADA applies to state and local governments (Title II) and businesses that are open to the public (Title III), which courts have consistently interpreted to include websites. In 2024, the Department of Justice (DOJ) issued a final rule for Title II, officially mandating <strong>WCAG 2.1 Level AA</strong> as the technical standard for state and local government websites.
            </p>
            <p className="text-gray-700">
              For private websites under Title III, there is no explicit technical specification in the law. However, the DOJ and U.S. courts consistently use WCAG 2.1 AA as the benchmark for determining if a website is accessible and therefore compliant with the ADA.
            </p>
          </div>
        </section>
        {/* How the ADA Checker Works */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How the achecker.ca ADA Checker Works</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Our <strong>ADA website compliance checker</strong> scans your URL and provides an Accessibility Score based on WCAG 2.1 AA success criteria. Each identified issue is a potential barrier for users with disabilities and therefore represents a potential ADA compliance risk. For example, missing alt text on an image is a WCAG failure that directly creates a barrier for a blind user, which could be cited in an ADA lawsuit.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-yellow-800">
                    <strong>Disclaimer:</strong> An automated scan is a critical first step, but it is not a legal guarantee of ADA compliance. Full conformance requires manual testing and a comprehensive accessibility strategy.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* ADA Website Compliance Checklist */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">ADA Website Compliance Checklist (Top Issues)</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              Our ADA accessibility audit helps you find and fix the most common and high-risk issues, including:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {adaChecklist.map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-gray-900">{item.issue}</span>
                    <span className="text-gray-700"> {item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* ADA vs WCAG */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">ADA vs. WCAG – How They Connect</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Think of it this way: The ADA is the law (the "what"), and WCAG is the technical standard used to meet it (the "how").
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {adaVsWcag.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">{item.type}:</h4>
                  <p className="text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800">
                Achieving WCAG 2.1 AA conformance is the most reliable method for ADA compliance. You can test this directly with our{' '}
                <Link to="/wcag-validator" className="font-medium underline hover:no-underline">
                  WCAG Validator & Compliance Checker
                </Link>
                . This approach is also consistent with other U.S. laws like Section 508, which mandates WCAG conformance for federal agencies.
              </p>
            </div>
          </div>
        </section>
        {/* Global Context */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Global Context (Why It Matters Beyond the US)</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700">
              While the ADA is a U.S. law, its reliance on WCAG makes this tool globally relevant. Accessibility laws in <strong>Canada (AODA, Accessible Canada Act)</strong>, the European Union (EN 301 549), Australia, and New Zealand are all based on the same WCAG principles. Fixing issues found by our ADA checker will simultaneously improve your compliance posture worldwide—including meeting Canadian federal and provincial accessibility requirements.
            </p>
          </div>
        </section>
        {/* FAQ Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions (FAQ)</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
        {/* CTA Section */}
        <section className="text-center py-12 bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Check Your ADA Compliance?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant WCAG 2.1 AA audit focused on ADA compliance risks and start protecting your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-red-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start ADA Compliance Check
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-red-700 text-white rounded-lg font-semibold hover:bg-red-800 transition-colors border border-red-500"
            >
              <Shield className="w-5 h-5 mr-2" />
              General Accessibility Checker
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
